import React, { useCallback, useEffect, useState } from 'react';
import { Card, Table, Tag, Select, Input, Space, Button, Typography } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import transferAdminService, { TRANSFER_TYPE_LABELS } from '../../services/transferAdminService';

const { Text } = Typography;

const ACTIONS = {
    request: ['blue', '发起'], accept: ['green', '接受'], decline: ['red', '拒绝'], cancel: ['default', '取消'],
    expire: ['default', '过期'], admin_transfer: ['purple', '管理员转移'],
};

const who = (u, id) => (u ? `${u.username} (#${u.id})` : (id ? `#${id}` : '-'));

/** 资源转移审计：谁、什么、从谁、给谁、何时、原因、转前/转后归属。 */
const TransferLogs = () => {
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [q, setQ] = useState({ page: 1, page_size: 20, type: undefined, action: undefined, keyword: '' });

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const d = await transferAdminService.logs({ ...q, keyword: q.keyword || undefined });
            setRows(Array.isArray(d?.items) ? d.items : []);
            setTotal(d?.total || 0);
        } catch (e) {
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [q]);

    useEffect(() => { load(); }, [load]);

    const columns = [
        { title: '时间', dataIndex: 'created_at', width: 170 },
        {
            title: '动作', dataIndex: 'action', width: 110,
            render: (a) => <Tag color={(ACTIONS[a] || [])[0]}>{(ACTIONS[a] || [null, a])[1]}</Tag>,
        },
        {
            title: '资源', key: 'res',
            render: (_, r) => (
                <Space direction="vertical" size={0}>
                    <span>{r.resource_name}</span>
                    <Text type="secondary" style={{ fontSize: 12 }}>{TRANSFER_TYPE_LABELS[r.resource_type] || r.resource_type} #{r.resource_id}</Text>
                </Space>
            ),
        },
        { title: '从', key: 'from', render: (_, r) => who(r.from_user, r.from_user_id) },
        { title: '给', key: 'to', render: (_, r) => who(r.to_user, r.to_user_id) },
        {
            title: '归属', key: 'owner', width: 120,
            render: (_, r) => (r.owner_before === r.owner_after ? <Text type="secondary">#{r.owner_before ?? '-'}</Text> : `#${r.owner_before} → #${r.owner_after}`),
        },
        {
            title: '操作人', key: 'actor', width: 160,
            render: (_, r) => (r.actor_type === 'system' ? <Text type="secondary">系统</Text> : `${r.actor_type === 'admin' ? '管理员 ' : ''}${who(r.actor, r.actor_id)}`),
        },
        { title: '原因', dataIndex: 'reason', ellipsis: true },
    ];

    return (
        <Card
            title="资源转移"
            extra={(
                <Space wrap>
                    <Select allowClear placeholder="类型" style={{ width: 120 }} value={q.type}
                        onChange={(v) => setQ({ ...q, type: v, page: 1 })}
                        options={Object.entries(TRANSFER_TYPE_LABELS).map(([value, label]) => ({ value, label }))} />
                    <Select allowClear placeholder="动作" style={{ width: 120 }} value={q.action}
                        onChange={(v) => setQ({ ...q, action: v, page: 1 })}
                        options={Object.entries(ACTIONS).map(([value, [, label]]) => ({ value, label }))} />
                    <Input.Search allowClear placeholder="资源名" style={{ width: 180 }} onSearch={(v) => setQ({ ...q, keyword: v, page: 1 })} />
                    <Button icon={<ReloadOutlined />} onClick={load} />
                </Space>
            )}
        >
            <Table
                rowKey="id" size="small" columns={columns} dataSource={rows} loading={loading} scroll={{ x: 1000 }}
                expandable={{
                    rowExpandable: (r) => !!r.details,
                    expandedRowRender: (r) => <pre style={{ margin: 0, fontSize: 12, whiteSpace: 'pre-wrap' }}>{JSON.stringify(r.details, null, 2)}</pre>,
                }}
                pagination={{
                    current: q.page, pageSize: q.page_size, total, showSizeChanger: true,
                    onChange: (page, page_size) => setQ({ ...q, page, page_size }),
                }}
            />
        </Card>
    );
};

export default TransferLogs;
